import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { socketService } from '../services/socket';
import { useAuthStore } from '../store/authStore';
import { useAiRunStore } from '../store/aiRunStore';
import { SERVICE_USAGE_QUERY_KEY } from './useServiceUsage';
import api from '../services/api';
import type { ApiResponse, User } from '../types/index';

export function useLiveUpdates() {
  const queryClient = useQueryClient();
  const userId = useAuthStore((s) => s.user?.id);
  const setUser = useAuthStore((s) => s.setUser);
  const updateActiveDetectorJob = useAiRunStore((s) => s.updateActiveDetectorJob);

  useEffect(() => {
    if (!userId) return;

    const socket = socketService.connect();

    // Re-sync the user (plan, bonus runs, storage) from server on mount/reconnect.
    // Handles the case where state drifted while the user was offline.
    const syncFromServer = () => {
      api.get<ApiResponse<{ user: User }>>('/auth/me')
        .then((r) => {
          const fresh = r.data.data?.user;
          if (fresh) setUser(fresh);
        })
        .catch(() => {});
    };

    const onConnect = () => {
      syncFromServer();
      // Reconciles any purchase/run fulfilled while the socket was disconnected.
      queryClient.invalidateQueries({ queryKey: SERVICE_USAGE_QUERY_KEY });
    };

    onConnect();
    socket.on('connect', onConnect);

    const onNewNotification = () => {
      queryClient.invalidateQueries({ queryKey: ['notifications-count'] });
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    };

    const onSubscriptionUpdate = () => {
      // Re-fetch the full user rather than patching state in place — the
      // socket payload only carries the new subscription's id, not the
      // updated storageLimitMb or anything else derived server-side.
      syncFromServer();
      queryClient.invalidateQueries({ queryKey: ['payment-intents'] });
      queryClient.invalidateQueries({ queryKey: ['my-subscriptions'] });
      queryClient.invalidateQueries({ queryKey: SERVICE_USAGE_QUERY_KEY }); // limits may have changed
    };

    const onRunCompleted = (data: { runId: string; tool: string; status: 'done' | 'error' }) => {
      // Only touch the tracked job if this event is actually about it — a
      // detector run completing for a *different* job (e.g. the user started
      // a new analysis before this one finished) must not falsely flip the
      // currently-tracked job to 'done'.
      if (data.tool === 'detector' && data.runId === useAiRunStore.getState().activeDetectorJob?.runId) {
        updateActiveDetectorJob({ status: data.status, progress: data.status === 'done' ? 100 : 0 });
      }
      // A completed run may have consumed a bonus run or changed the daily count — resync.
      syncFromServer();
      queryClient.invalidateQueries({ queryKey: SERVICE_USAGE_QUERY_KEY });
      // Refresh whichever run list/detail views are on screen — without this,
      // table/history rows keep showing "Running" until their staleTime lapses.
      queryClient.invalidateQueries({ queryKey: ['ai-runs'] });
      queryClient.invalidateQueries({ queryKey: ['ai-run', data.runId] });
    };

    const onRunUnlockFulfilled = () => {
      // A Pay-per-Run purchase was fulfilled via the webhook — resync bonusRuns.
      syncFromServer();
      queryClient.invalidateQueries({ queryKey: SERVICE_USAGE_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: ['payment-intents'] });
    };

    socket.on('new_notification', onNewNotification);
    socket.on('subscription_update', onSubscriptionUpdate);
    socket.on('run_completed', onRunCompleted);
    socket.on('run_unlock_fulfilled', onRunUnlockFulfilled);

    return () => {
      socket.off('connect', onConnect);
      socket.off('new_notification', onNewNotification);
      socket.off('subscription_update', onSubscriptionUpdate);
      socket.off('run_completed', onRunCompleted);
      socket.off('run_unlock_fulfilled', onRunUnlockFulfilled);
      // Do NOT disconnect here — the socket is a singleton shared across re-renders.
      // Disconnection only happens on logout (see useAuth.ts).
    };
  }, [userId, queryClient, setUser, updateActiveDetectorJob]);
}
